import { Autocomplete, Chip, TextField } from "@mui/material";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import * as React from "react";

import { useDomainServices } from "@/providers/DomainServices";
import type { ResolvedOptions } from "@/shared/components/filters/FilterDefinition";
import { useDebouncedCallback } from "@/shared/hooks/useDebouncedCallback";

import {
  buildOrgOptionResolver,
  buildUserOptionResolver,
} from "../ExploreAgentThreads/filterOptionResolvers";

export interface MemberOption {
  label: string;
  value: string;
}

const SEARCH_DEBOUNCE_MS = 300;

// Shorter terms are held back: the search endpoints match by substring, and
// one or two characters match nearly everything. Until the term is long
// enough (and while a longer one is debounced), the previous page stays
// loaded and the typed text narrows it client-side.
const MIN_SEARCH_LENGTH = 3;

function toMemberOptions(
  resolved: ResolvedOptions,
  kind: "User" | "Org",
): MemberOption[] {
  return resolved.options.map((option) => {
    const { label, value } =
      typeof option === "string" ? { label: option, value: option } : option;
    return { label: `${kind}: ${label}`, value };
  });
}

interface UserGroupMemberPickerProps {
  value: MemberOption[];
  onChange: (next: MemberOption[]) => void;
}

/**
 * Multi-select of users and orgs for a user group's membership.
 *
 * Options come from the admin user and org search endpoints, queried only
 * while the dropdown is open and re-queried (debounced) as the admin types.
 * Each search returns a capped page, so the picker never loads the full
 * directory. Selected members keep the label they were picked with; members
 * loaded with an existing group carry only their id.
 */
export function UserGroupMemberPicker({
  value,
  onChange,
}: UserGroupMemberPickerProps) {
  const { adminService, orgService } = useDomainServices();
  const queryClient = useQueryClient();
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const debounceSearch = useDebouncedCallback(setSearch, SEARCH_DEBOUNCE_MS);

  const resolveUsers = React.useMemo(
    () => buildUserOptionResolver(queryClient, adminService),
    [queryClient, adminService],
  );
  const resolveOrgs = React.useMemo(
    () => buildOrgOptionResolver(queryClient, orgService),
    [queryClient, orgService],
  );

  const query = useQuery({
    queryKey: ["user-group-member-options", search],
    queryFn: async ({ signal }) => {
      const [users, orgs] = await Promise.all([
        resolveUsers(search, signal),
        resolveOrgs(search, signal),
      ]);
      return {
        options: [
          ...toMemberOptions(users, "User"),
          ...toMemberOptions(orgs, "Org"),
        ],
        truncated: Boolean(users.truncated || orgs.truncated),
      };
    },
    enabled:
      open && (search.length === 0 || search.length >= MIN_SEARCH_LENGTH),
    placeholderData: keepPreviousData,
  });

  const truncated = query.data?.truncated ?? false;

  return (
    <Autocomplete<MemberOption, true, false, false>
      multiple
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={query.data?.options ?? []}
      // MUI's default substring filter on the label narrows the loaded page by
      // the typed text. Every server match also matches its label (which
      // holds both name and id), so this never hides a real result.
      loading={query.isFetching}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      value={value}
      onChange={(_, newValue) => onChange(newValue)}
      onInputChange={(_, inputValue, reason) => {
        if (reason === "input") {
          debounceSearch(inputValue.trim());
        } else {
          debounceSearch.cancel();
          setSearch("");
        }
      }}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((opt, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return (
            <Chip key={key} label={opt.label} size="small" {...tagProps} />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label="Members"
          placeholder="Search users and orgs…"
          helperText={
            truncated
              ? "Showing the first matches. Type more to narrow the search."
              : undefined
          }
        />
      )}
    />
  );
}
